import { Fragment, useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { SuggestedChangesView } from '@/components/SuggestChanges';
import { useDepositsSuggestChanges } from 'src/context/suggest-changes';
import { Button } from '@/components/ui/button';
import { ClientError, getApiError } from 'src/utils/errors';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { getRequestChangesExitPath } from 'src/pages/SoftwareEscrowRequestChanges/lib';
import { SoftwareEscrowRequestChangesRoutesEnum } from 'src/pages/SoftwareEscrowRequestChanges/model';

const SubmitChangesDialog = () => {
  const { sendSuggestedChanges, allDeletedAssets } = useDepositsSuggestChanges();
  const {
    softwareEscrow: { id: softwareEscrowId },
    refetch,
  } = useSoftwareEscrow();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState(false);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);

  const onSubmit = async () => {
    setIsLoading(true);

    try {
      const data = await sendSuggestedChanges();

      if (data?.suggestedChangeCreate?.success) {
        setIsAlertDialogOpen(false);
        setIsConfirmationDialogOpen(true);
      } else {
        throw new ClientError(data?.suggestedChangeCreate?.errors?.[0]);
      }
    } catch (error) {
      getApiError(error, toast.error);
    } finally {
      setIsLoading(false);
    }
  };

  const suggestedChangesItems = allDeletedAssets.map((asset) => ({
    label: asset.name,
    value: 'removed from escrow',
  }));

  const onConfirmationDialogOpenChange = async (open: boolean) => {
    setIsConfirmationDialogOpen(open);

    if (!open) {
      await navigate(getRequestChangesExitPath(softwareEscrowId, SoftwareEscrowRequestChangesRoutesEnum.Deposits));
      refetch();
    }
  };

  return (
    <Fragment>
      <AlertDialog open={isAlertDialogOpen} onOpenChange={setIsAlertDialogOpen}>
        <AlertDialogTrigger asChild>
          <Button disabled={!allDeletedAssets.length}>Submit changes</Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to submit these changes?</AlertDialogTitle>
            <AlertDialogDescription>
              These changes will be sent for Codekeeper’s review. Depending on the nature of the suggestion, the changes
              may require the counterpart’s permission before they can be applied, and signed agreements may have to be
              legally amended.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="overflow-hidden">
            <SuggestedChangesView defaultOpen items={suggestedChangesItems} />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button disabled={isLoading} isLoading={isLoading} onClick={onSubmit}>
              Submit
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={isConfirmationDialogOpen} onOpenChange={onConfirmationDialogOpenChange}>
        <DialogContent className="sm:max-w-[441px]">
          <DialogHeader className="gap-1.5 text-center">
            <DialogTitle>Suggested changes sent for review!</DialogTitle>
            <DialogDescription>
              We will notify you of the outcome as soon as the review has been completed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button className="w-full">Back to escrow</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Fragment>
  );
};

export default SubmitChangesDialog;
